import { useEffect, useId, useRef, useState } from 'react';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { Field } from '@/components/ui/Field';
import { TimeInput } from '@/components/ui/TimeInput';
import { joinDatetimeLocal, splitDatetimeLocal } from '@/lib/datetimeLocal';

/**
 * Danish civil date (`dd.mm.yyyy`) plus a 24-hour clock.
 * `value` and `onChange` use `YYYY-MM-DDTHH:mm`, the same wall-clock string
 * `<input type="datetime-local">` produced, so the stored ISO instant is unchanged.
 * A date or a time on its own is kept on screen and does not emit a partial value.
 */
export function DanishDateTimeField({
  value,
  onChange,
  label,
  name,
  disabled,
  required,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  name?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}) {
  const autoId = useId();
  const dateName = name ? `${name}-date` : `${autoId}-date`;
  const timeName = name ? `${name}-time` : `${autoId}-time`;
  const initial = splitDatetimeLocal(value);
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const emitted = useRef(value);

  useEffect(() => {
    if (value === emitted.current) return;
    const next = splitDatetimeLocal(value);
    setDate(next.date);
    setTime(next.time);
    emitted.current = value;
  }, [value]);

  function publish(nextDate: string, nextTime: string) {
    setDate(nextDate);
    setTime(nextTime);
    const joined = joinDatetimeLocal(nextDate, nextTime);
    emitted.current = joined;
    if (joined !== value) onChange(joined);
  }

  return (
    <div className={className} data-testid="danish-datetime-field">
      <Field label={label} htmlFor={dateName} required={required}>
        <div className="flex flex-wrap items-start gap-s3">
          <div className="w-full min-w-[11rem] max-w-xs">
            <DanishDateField
              name={dateName}
              value={date}
              disabled={disabled}
              required={required}
              calendar
              onChange={(nextDate) => publish(nextDate, time)}
            />
          </div>
          <TimeInput
            name={timeName}
            value={time}
            disabled={disabled}
            allowEmpty
            onChange={(nextTime) => publish(date, nextTime)}
          />
        </div>
      </Field>
    </div>
  );
}
